import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Anchor } from 'lucide-react';

/**
 * Pirámide Olfativa Visual - Diseño de tarjetas apiladas 3D
 * Tarjetas superpuestas que se despliegan al hacer hover
 */
const OlfactoryPyramid = ({ notesTop, notesHeart, notesBase }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Si no hay notas, no renderizar
  if (!notesTop && !notesHeart && !notesBase) return null;

  const cards = [
    {
      id: 'top',
      label: 'Notas de Entrada',
      subtitle: 'Primeras impresiones • 0-15 min',
      notes: notesTop,
      icon: Sparkles,
      gradient: 'from-brand-cobalt/[0.06] to-transparent',
      borderColor: 'border-brand-cobalt/30',
      glowColor: 'shadow-brand-cobalt',
      iconBg: 'bg-brand-cobalt',
      textColor: 'text-brand-cobalt',
      dividerColor: 'via-brand-cobalt/40',
    },
    {
      id: 'heart',
      label: 'Notas de Corazón',
      subtitle: 'El alma del perfume • 15 min - 3h',
      notes: notesHeart,
      icon: Heart,
      gradient: 'from-brand-magenta/[0.06] to-transparent',
      borderColor: 'border-brand-magenta/30',
      glowColor: 'shadow-brand-magenta',
      iconBg: 'bg-brand-magenta',
      textColor: 'text-brand-magenta',
      dividerColor: 'via-brand-magenta/40',
    },
    {
      id: 'base',
      label: 'Notas de Base',
      subtitle: 'Duración prolongada • 3h+',
      notes: notesBase,
      icon: Anchor,
      gradient: 'from-brand-grape/[0.06] to-transparent',
      borderColor: 'border-brand-grape/30',
      glowColor: 'shadow-brand-soft',
      iconBg: 'bg-brand-grape',
      textColor: 'text-brand-grape',
      dividerColor: 'via-brand-grape/40',
    },
  ];

  // Filtrar solo las tarjetas que tienen notas
  const activeCards = cards.filter(card => card.notes && card.notes.trim());

  if (activeCards.length === 0) return null;

  return (
    <div className="mt-6">
      {/* Título */}
      <div className="flex items-center gap-2 mb-6">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-brand-cobalt/40 to-transparent" />
        <h3 className="text-sm uppercase tracking-widest text-brand-black/70 font-medium px-3">
          Pirámide Olfativa
        </h3>
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-brand-magenta/40 to-transparent" />
      </div>

      {/* Contenedor de tarjetas apiladas */}
      <div 
        className="relative cursor-pointer"
        style={{ height: isExpanded ? `${activeCards.length * 120}px` : '140px' }}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {activeCards.map((card, index) => {
          const Icon = card.icon;
          const yOffset = isExpanded 
            ? index * 115 
            : index * 8;
          
          return (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ 
                opacity: 1, 
                y: yOffset,
                rotateX: isExpanded ? 0 : index * 2,
                scale: isExpanded ? 1 : 1 - (index * 0.02),
              }}
              transition={{ 
                duration: 0.4, 
                delay: index * 0.05,
                ease: [0.23, 1, 0.32, 1]
              }}
              className="absolute left-0 right-0"
              style={{ zIndex: 30 - index * 10 }}
            >
              <div
                className={`
                  relative overflow-hidden rounded-xl
                  bg-white bg-gradient-to-br ${card.gradient}
                  border ${card.borderColor}
                  transition-all duration-300
                  ${isExpanded ? `shadow-lg ${card.glowColor}` : 'shadow-brand-soft'}
                `}
              >
                {/* Contenido */}
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Ícono */}
                    <div
                      className={`
                        p-3 rounded-xl ${card.iconBg}
                        shadow-lg transition-transform duration-300
                        ${isExpanded ? 'scale-110' : ''}
                      `}
                    >
                      <Icon size={20} className="text-white" />
                    </div>

                    {/* Texto */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-sm font-bold ${card.textColor}`}>
                          {card.label}
                        </span>
                      </div>
                      <p className="text-[11px] text-brand-black/40 mb-2">
                        {card.subtitle}
                      </p>
                      <p className="text-sm text-brand-black/80 leading-relaxed">
                        {card.notes}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Línea decorativa inferior */}
                <div className={`absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent ${card.dividerColor} to-transparent`} />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Indicador de interacción */}
      <motion.p
        className="text-center text-[11px] text-brand-black/40 mt-4"
        animate={{ opacity: isExpanded ? 0 : 1 }}
      >
        {isExpanded ? '' : 'Toca para expandir'}
      </motion.p>
    </div>
  );
};

export default OlfactoryPyramid;
